import { connect } from 'react-redux';
import { compose } from 'redux';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';

import { withTranslate } from 'components/hocs';
import { prepareButtons } from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { imagesURL } from 'config';
import { isOnline } from 'mobile-modules/offline';
import { resolveOfflineAssetKeySync } from 'mobile-modules/offline/assets';

// components
import WhiteBox from 'components/flink-play/WhiteBox/WhiteBox';
import { QuickStartVideos, VideoAnimation } from 'components/flink-play';

// button types
import {
  CREDITS,
  QUICKSTART_VIDEOS,
  HELP,
  PARENT_ADVICE,
} from 'consts/buttons';

// css
import classes from './HelpControls.module.scss';

// actions
import { viewLoginPageDocument } from "actions/commonActions";
import {
  showLoginFlinkPlayHelp,
  showParentAdvice,
  showQuickstartVideos,
} from "actions/flinkPlayActions";

const normalizeLocaleKey = (value = '') => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/_/g, '-');

const getAndroidPdfRenderPlugin = () => (
  typeof window !== 'undefined'
    ? window.Capacitor?.Plugins?.FLCPdfRender || null
    : null
);

const getDocumentsWithFallback = (fileMap = {}, locale = {}) => {
  /*
  Filemap:
  {locale: fileId}

  Mobile Credits may have a localized document configured while the underlying
  object is no longer available. Keep the requested locale first, but retain an
  English candidate so the click can recover instead of opening a blank viewer.
  */
  const requested = normalizeLocaleKey(locale.code);
  const requestedBase = requested.split('-')[0];
  const availableKeys = Object.keys(fileMap || {});
  const candidates = [requested, requestedBase, 'en'];
  const seen = new Set();
  const documents = [];

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const matchedKey = availableKeys.find((key) => {
      const normalizedKey = normalizeLocaleKey(key);
      return normalizedKey === candidate
        || (candidate.length === 2 && normalizedKey.split('-')[0] === candidate);
    });
    if (!matchedKey || seen.has(matchedKey)) return;

    const documentId = fileMap[matchedKey];
    if (!documentId) return;
    seen.add(matchedKey);
    documents.push({ id: documentId, locale: matchedKey });
  });

  return documents;
};

const resolveQuickStartLanguage = (documents, lang) => {
  const quickStartByLocale = documents?.quickStart || {};
  const availableKeys = Object.keys(quickStartByLocale).filter(
    (key) => Array.isArray(quickStartByLocale[key]) && quickStartByLocale[key].length
  );

  const requested = normalizeLocaleKey(lang?.code || 'en');
  const base = requested.split('-')[0];
  const candidates = [requested, base, 'en'];

  const matchedKey = candidates
    .map((candidate) => {
      const normalizedCandidate = normalizeLocaleKey(candidate);
      const candidateBase = normalizedCandidate.split('-')[0];
      return availableKeys.find((key) => {
        const normalizedKey = normalizeLocaleKey(key);
        return normalizedKey === normalizedCandidate
          || normalizedKey.split('-')[0] === candidateBase;
      });
    })
    .find(Boolean);

  if (!matchedKey) return lang;

  const matchedBase = normalizeLocaleKey(matchedKey).split('-')[0];
  const requestedBase = requested.split('-')[0];

  if (matchedBase === requestedBase) {
    return { ...(lang || {}), code: matchedKey };
  }

  if (matchedBase === 'en') {
    return { ...(lang || {}), code: matchedKey, name: 'English', displayName: 'English' };
  }

  return { ...(lang || {}), code: matchedKey };
};

const HelpControls = ({
  translate,
  video,
  quickStartVideos,
  globalButtons,
  lang,
  documents,
  loginHelpStringNumber,
  compactMobileHelp,
  hideCredits,
  mobileParentTips,
  mobileParentTipsAvailable,
  parentAdvice,
  viewLoginPageDocument,
  showLoginFlinkPlayHelp,
  showParentAdvice,
  showQuickstartVideos,
}) => {
  const [mobileDocumentUrl, setMobileDocumentUrl] = useState('');
  const [androidPdfPages, setAndroidPdfPages] = useState([]);
  const [androidPdfLoading, setAndroidPdfLoading] = useState(false);
  const [androidPdfError, setAndroidPdfError] = useState('');
  const androidPdfSessionRef = useRef('');
  const androidPdfRenderTokenRef = useRef(0);

  const closeAndroidPdfSession = useCallback(async () => {
    androidPdfRenderTokenRef.current += 1;
    const sessionId = androidPdfSessionRef.current;
    androidPdfSessionRef.current = '';
    if (!sessionId) return;

    const plugin = getAndroidPdfRenderPlugin();
    if (!plugin?.close) return;
    try {
      await plugin.close({ sessionId });
    } catch (error) {
      // Closing Credits must never block the Login UI.
    }
  }, []);

  const renderAndroidPdf = useCallback(async (url) => {
    const plugin = getAndroidPdfRenderPlugin();
    const renderToken = androidPdfRenderTokenRef.current + 1;
    androidPdfRenderTokenRef.current = renderToken;

    setAndroidPdfPages([]);
    setAndroidPdfError('');
    setAndroidPdfLoading(true);

    if (!plugin?.open || !plugin?.renderPage) {
      setAndroidPdfLoading(false);
      setAndroidPdfError('Credits viewer is unavailable in this Android build.');
      return;
    }

    try {
      const previousSessionId = androidPdfSessionRef.current;
      if (previousSessionId && plugin.close) {
        try {
          await plugin.close({ sessionId: previousSessionId });
        } catch (error) {
          // Continue with the new document even if stale-session cleanup failed.
        }
      }

      if (androidPdfRenderTokenRef.current !== renderToken) return;

      const opened = await plugin.open({ url });
      const sessionId = opened?.sessionId || '';
      const pageCount = Number(opened?.pageCount || 0);
      if (!sessionId || pageCount < 1) throw new Error('PDF did not return any pages.');

      if (androidPdfRenderTokenRef.current !== renderToken) {
        if (plugin.close) {
          try {
            await plugin.close({ sessionId });
          } catch (error) {
            // The overlay was closed while the native renderer was opening.
          }
        }
        return;
      }

      androidPdfSessionRef.current = sessionId;
      const pixelRatio = Math.max(1, Math.min(2, Number(window.devicePixelRatio || 1)));
      const cssWidth = Math.max(640, Math.round((window.innerWidth || 1024) * 0.96));
      const renderWidth = Math.min(1800, Math.round(cssWidth * pixelRatio));

      for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
        if (androidPdfRenderTokenRef.current !== renderToken) return;
        const page = await plugin.renderPage({
          sessionId,
          pageIndex,
          width: renderWidth,
        });
        if (androidPdfRenderTokenRef.current !== renderToken) return;
        if (page?.dataUrl) {
          setAndroidPdfPages((currentPages) => [
            ...currentPages,
            { pageIndex, dataUrl: page.dataUrl },
          ]);
        }
      }

      if (androidPdfRenderTokenRef.current === renderToken) {
        setAndroidPdfLoading(false);
      }
    } catch (error) {
      if (androidPdfRenderTokenRef.current !== renderToken) return;
      setAndroidPdfLoading(false);
      setAndroidPdfError(error?.message || 'Unable to display Credits.');
    }
  }, []);

  const closeMobileDocument = useCallback(() => {
    setMobileDocumentUrl('');
    setAndroidPdfPages([]);
    setAndroidPdfLoading(false);
    setAndroidPdfError('');
    closeAndroidPdfSession();
  }, [closeAndroidPdfSession]);

  useEffect(() => () => {
    closeAndroidPdfSession();
  }, [closeAndroidPdfSession]);

  const openLoginDocument = useCallback(async (key, documentCandidates) => {
    const candidates = Array.isArray(documentCandidates)
      ? documentCandidates
      : [documentCandidates];

    for (const candidate of candidates) {
      if (!candidate?.locale || !candidate?.id) continue;
      const url = await viewLoginPageDocument(
        key,
        candidate.locale,
        candidate.id,
        !compactMobileHelp,
      );
      if (!url) continue;

      if (compactMobileHelp) {
        setMobileDocumentUrl(url);
        if (getMobileAppPlatform() === 'android') {
          renderAndroidPdf(url);
        }
      }
      return url;
    }

    return null;
  }, [compactMobileHelp, renderAndroidPdf, viewLoginPageDocument]);

  const quickStartLanguage = useMemo(() => (
    compactMobileHelp
      ? resolveQuickStartLanguage(documents || {}, lang)
      : lang
  ), [compactMobileHelp, documents, lang]);

  const buttons = useMemo(() => {
    const safeDocuments = documents || {};
    const mobileButtonImage = (type) => {
      if (!compactMobileHelp) return undefined;
      const button = (globalButtons || []).find((item) => item?.alias === type);
      if (!button?.imageFilename) return undefined;
      if (isOnline()) return `${imagesURL}/GlobalButtons/${button.imageFilename}`;
      // Passing a non-empty local value prevents prepareButtons from silently
      // falling back to a remote URL while the app is offline.
      return resolveOfflineAssetKeySync(`Images/GlobalButtons/${button.imageFilename}`) || 'about:blank';
    };
    const quickStartHelpVideos = compactMobileHelp
      ? (safeDocuments.quickStart?.[quickStartLanguage?.code] || [])
      : (safeDocuments.quickStart?.[lang?.code] || []);
    const creditsHelpDocs = getDocumentsWithFallback(safeDocuments.credits || {}, lang || {});
    const creditsHelpDoc = creditsHelpDocs[0] || null;

    return prepareButtons(
      [
        {
          type: CREDITS,
          imageSrc: mobileButtonImage(CREDITS),
          position: compactMobileHelp ? 1 : undefined,
          onClick: () => openLoginDocument('credits', creditsHelpDocs),
          dontShow: hideCredits || !creditsHelpDoc,
        },
        {
          type: QUICKSTART_VIDEOS,
          imageSrc: mobileButtonImage(QUICKSTART_VIDEOS),
          position: compactMobileHelp ? 2 : undefined,
          onClick: () => showQuickstartVideos(true),
          dontShow: compactMobileHelp ? false : !quickStartHelpVideos?.length,
        },
        {
          type: HELP,
          imageSrc: mobileButtonImage(HELP),
          position: compactMobileHelp ? 3 : undefined,
          onClick: () => showLoginFlinkPlayHelp({
            stringNumber: loginHelpStringNumber,
            compactMobile: compactMobileHelp,
          }),
        },
        {
          type: PARENT_ADVICE,
          imageSrc: mobileButtonImage(PARENT_ADVICE),
          position: compactMobileHelp ? 4 : undefined,
          titleStringNumber: 764,
          titleName: 'Parent Tip',
          onClick: () => showParentAdvice(!parentAdvice),
          dontShow: compactMobileHelp ? false : (!mobileParentTips || !mobileParentTipsAvailable),
        },
      ],
      globalButtons,
      translate,
    ).filter(button => button);
  }, [
    globalButtons, translate,
    documents, lang, quickStartLanguage, openLoginDocument,
    loginHelpStringNumber, compactMobileHelp, hideCredits, showLoginFlinkPlayHelp,
    mobileParentTips, mobileParentTipsAvailable,
    parentAdvice, showParentAdvice,
    showQuickstartVideos,
  ]);

  return (
    <>
      <WhiteBox
        innerClass={`${classes.innerClass} ${compactMobileHelp ? classes.mobileInnerClass : ''}`}
        outerClass={`${classes.outerClass} ${compactMobileHelp ? classes.mobileOuterClass : ''}`}
      >
        {buttons.map((button, index) => <div key={index}>
          {button}
        </div>)}
      </WhiteBox>
      {/* QuickStart videos modal */}
      {quickStartVideos && <QuickStartVideos language={compactMobileHelp ? quickStartLanguage : lang} />}
      {video && <VideoAnimation />}
      {compactMobileHelp && mobileDocumentUrl && (
        <div className={classes.mobileDocumentOverlay} role="dialog" aria-modal="true" aria-label="Credits">
          <div className={classes.mobileDocumentHeader}>
            <button
              type="button"
              className={classes.mobileDocumentClose}
              onClick={closeMobileDocument}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          {getMobileAppPlatform() === 'android' ? (
            <div
              className={classes.mobileDocumentFrame}
              style={{
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                display: 'block',
                boxSizing: 'border-box',
                padding: androidPdfPages.length ? '8px 12px' : 0,
              }}
            >
              {androidPdfLoading && !androidPdfPages.length && (
                <div style={{ padding: '20px', textAlign: 'center' }}>Loading Credits...</div>
              )}
              {androidPdfError && (
                <div style={{ padding: '20px', textAlign: 'center' }}>{androidPdfError}</div>
              )}
              {androidPdfPages.map((page) => (
                <img
                  key={page.pageIndex}
                  src={page.dataUrl}
                  alt={`Credits page ${page.pageIndex + 1}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    margin: androidPdfPages.length > 1 ? '0 auto 10px' : '-64px auto 0',
                    background: '#fff',
                  }}
                />
              ))}
            </div>
          ) : (
            <iframe
              className={classes.mobileDocumentFrame}
              src={mobileDocumentUrl}
              title="Credits"
            />
          )}
        </div>
      )}
    </>
  );
}

const mapStateToProps = ({ flinkPlay, status, common }) => ({
  globalButtons: common.globalButtons,
  documents: common.config?.loginPageDocuments || {},
  video: flinkPlay.video,
  quickStartVideos: flinkPlay.quickStartVideos,
  parentAdvice: !!flinkPlay.parentAdvice,
  lang: status.lang,
});

export default compose(
  withTranslate,
  connect(mapStateToProps, {
    viewLoginPageDocument,
    showLoginFlinkPlayHelp,
    showParentAdvice,
    showQuickstartVideos,
  }),
)(HelpControls);
